import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, FileText } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  onNext: (contractUrl?: string) => void;
  onBack: () => void;
  userType: 'guide' | 'organizer';
  signature: boolean;
  onSignatureChange: (value: boolean) => void;
};

// Static contract URLs for different languages
const CONTRACT_URLS = {
  en: '/contracts/english-agreement.pdf',
  ar: '/contracts/arabic-agreement.pdf'
};

export const ContractStep = ({
  onNext,
  onBack,
  userType,
  signature,
  onSignatureChange
}: Props) => {
  const { translations } = useLanguage();
  const [selectedContractUrl, setSelectedContractUrl] = useState(
    CONTRACT_URLS.en
  );

  const handleSelectContract = (url: string) => {
    setSelectedContractUrl(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h2 className="text-2xl font-bold">
        {translations?.contractStep?.headers?.contractReview || 'Contract Review'}
      </h2>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden border">
        <div className="p-6 border-b bg-gray-50">
          <h3 className="font-semibold text-lg mb-4">
            {translations?.contractStep?.headers?.contractName ||
              'Marketing Services Agreement'}
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            {translations?.contractStep?.subHeaders?.reviewMessage ||
              'Please review the contract before signing'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={CONTRACT_URLS.en}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSelectContract(CONTRACT_URLS.en)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <ExternalLink size={20} />
              <span>View English Contract</span>
            </a>
            <a
              href={CONTRACT_URLS.ar}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSelectContract(CONTRACT_URLS.ar)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <ExternalLink size={20} />
              <span>عرض العقد باللغة العربية</span>
            </a>
          </div>
        </div>

        <div className="p-6">
          <h3 className="font-semibold text-lg mb-4">
            {translations?.verification?.digitalSignature || 'Digital Signature'}
          </h3>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={signature}
              onChange={(e) => onSignatureChange(e.target.checked)}
              className="mt-1 rounded text-primary-500 focus:ring-primary-500"
            />
            <label className="text-sm text-gray-700">
              {translations?.contractStep?.labels?.digitalSignature ||
                'I have read the contract and agree to sign it'}
            </label>
          </div>
        </div>

        <div className="p-6 pt-0">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              onNext(selectedContractUrl);
            }}
            disabled={!signature}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white transition-colors ${
              !signature
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600'
            }`}
          >
            <FileText size={20} />
            <span>
              {userType === 'organizer'
                ? translations?.contractStep?.buttons?.next || 'Next'
                : translations?.contractStep?.buttons?.submit || 'Submit'}
            </span>
          </motion.button>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="px-6 py-2 text-primary-500 border border-primary-500 rounded-lg hover:bg-primary-50"
        >
          {translations?.contractStep?.buttons?.back || 'Back'}
        </motion.button>
      </div>
    </motion.div>
  );
};
